// SemanticVectorSpace.ts: Vector space models for semantic representations
// Implements semantic similarity, clustering, and dimensional reduction

export interface ConceptVector {
    conceptId: string;
    name: string;
    dimensions: number[];      // N-dimensional vector
    magnitude: number;          // L2 norm
    normalized: number[];       // Normalized vector
}

export interface SemanticSpace {
    name: string;
    dimensionNames: string[];   // Names for each dimension
    concepts: Map<string, ConceptVector>;
    dimensions: number;          // Number of dimensions
}

export interface SimilarityMetrics {
    cosine: number;              // Cosine similarity (0-1)
    euclidean: number;             // Euclidean distance
    manhattan: number;             // Manhattan distance (L1 norm)
    jaccard: number;            // Jaccard similarity (for set representations)
    pearson: number;            // Pearson correlation
}

export interface ClusterAssignment {
    conceptId: string;
    clusterId: number;
    distance: number;
    confidence: number;
}

export class SemanticVectorSpace {
    private semanticSpaces: Map<string, SemanticSpace> = new Map();
    private currentSpace: string = 'default';

    constructor() {
        this.createDefaultSemanticSpace();
    }

    private createDefaultSemanticSpace(): void {
        const dimensionNames = [
            'concreteness', 'emotionality', 'urgency', 'intimacy', 'complexity',
            'transcendence', 'unity', 'growth', 'wisdom', 'beauty'
        ];
        
        const space: SemanticSpace = {
            name: 'default',
            dimensionNames,
            concepts: new Map(),
            dimensions: dimensionNames.length
        };
        
        this.semanticSpaces.set('default', space);
    }

    // Add or update concept vector
    public addConcept(spaceName: string, conceptId: string, name: string, dimensions: number[]): void {
        const space = this.semanticSpaces.get(spaceName);
        if (!space) {
            console.error(`Semantic space '${spaceName}' not found`);
            return;
        }
        
        if (dimensions.length !== space.dimensions) {
            console.error(`Dimension mismatch: expected ${space.dimensions}, got ${dimensions.length}`);
            return;
        }
        
        const magnitude = this.calculateMagnitude(dimensions);
        const normalized = this.normalize(dimensions);
        
        const vector: ConceptVector = {
            conceptId,
            name,
            dimensions: [...dimensions],
            magnitude,
            normalized
        };
        
        space.concepts.set(conceptId, vector);
    }

    // Calculate L2 norm (magnitude)
    private calculateMagnitude(vector: number[]): number {
        const sumSquares = vector.reduce((sum, val) => sum + val * val, 0);
        return Math.sqrt(sumSquares);
    }

    // Normalize vector to unit length
    private normalize(vector: number[]): number[] {
        const magnitude = this.calculateMagnitude(vector);
        if (magnitude === 0) {
            return vector.map(() => 0);
        }
        return vector.map(val => val / magnitude);
    }

    // Calculate similarity metrics between two concepts
    public calculateSimilarity(spaceName: string, conceptId1: string, conceptId2: string): SimilarityMetrics | null {
        const space = this.semanticSpaces.get(spaceName);
        if (!space) return null;
        
        const vector1 = space.concepts.get(conceptId1);
        const vector2 = space.concepts.get(conceptId2);
        
        if (!vector1 || !vector2) return null;
        
        // Cosine similarity
        const cosine = this.cosineSimilarity(vector1.dimensions, vector2.dimensions);
        
        // Euclidean distance
        const euclidean = this.euclideanDistance(vector1.dimensions, vector2.dimensions);
        
        // Manhattan distance
        const manhattan = this.manhattanDistance(vector1.dimensions, vector2.dimensions);
        
        // Jaccard similarity (convert to binary representation)
        const jaccard = this.jaccardSimilarity(vector1.dimensions, vector2.dimensions);
        
        // Pearson correlation
        const pearson = this.pearsonCorrelation(vector1.dimensions, vector2.dimensions);
        
        return {
            cosine,
            euclidean,
            manhattan,
            jaccard,
            pearson
        };
    }

    // Cosine similarity: dot product / (|a| * |b|)
    private cosineSimilarity(vec1: number[], vec2: number[]): number {
        const dotProduct = vec1.reduce((sum, val, i) => sum + val * vec2[i], 0);
        const mag1 = this.calculateMagnitude(vec1);
        const mag2 = this.calculateMagnitude(vec2);
        
        if (mag1 === 0 || mag2 === 0) return 0;
        
        return dotProduct / (mag1 * mag2);
    }

    // Euclidean distance: sqrt(sum((ai - bi)^2))
    private euclideanDistance(vec1: number[], vec2: number[]): number {
        const sumSquares = vec1.reduce((sum, val, i) => sum + Math.pow(val - vec2[i], 2), 0);
        return Math.sqrt(sumSquares);
    }

    // Manhattan distance: sum(|ai - bi|)
    private manhattanDistance(vec1: number[], vec2: number[]): number {
        return vec1.reduce((sum, val, i) => sum + Math.abs(val - vec2[i]), 0);
    }

    // Jaccard similarity: intersection / union (binary representation)
    private jaccardSimilarity(vec1: number[], vec2: number[]): number {
        const threshold = 0.5;  // Threshold for binary conversion
        const set1 = new Set(vec1.map(v => v > threshold));
        const set2 = new Set(vec2.map(v => v > threshold));
        
        const intersection = new Set([...set1].filter(x => set2.has(x)));
        const union = new Set([...set1, ...set2]);
        
        return union.size === 0 ? 0 : intersection.size / union.size;
    }

    // Pearson correlation coefficient
    private pearsonCorrelation(vec1: number[], vec2: number[]): number {
        const n = vec1.length;
        const mean1 = vec1.reduce((a, b) => a + b, 0) / n;
        const mean2 = vec2.reduce((a, b) => a + b, 0) / n;
        
        let numerator = 0;
        let sumSquares1 = 0;
        let sumSquares2 = 0;
        
        for (let i = 0; i < n; i++) {
            const diff1 = vec1[i] - mean1;
            const diff2 = vec2[i] - mean2;
            numerator += diff1 * diff2;
            sumSquares1 += diff1 * diff1;
            sumSquares2 += diff2 * diff2;
        }
        
        const denominator = Math.sqrt(sumSquares1 * sumSquares2);
        return denominator === 0 ? 0 : numerator / denominator;
    }

    // Find most similar concepts to a given concept
    public findMostSimilar(spaceName: string, conceptId: string, limit: number = 10): Array<{
        conceptId: string;
        name: string;
        cosine: number;
        euclidean: number;
    }> {
        const space = this.semanticSpaces.get(spaceName);
        if (!space) return [];
        
        const targetVector = space.concepts.get(conceptId);
        if (!targetVector) return [];
        
        const similarities: Array<{
            conceptId: string;
            name: string;
            cosine: number;
            euclidean: number;
        }> = [];
        
        for (const [id, vector] of space.concepts) {
            if (id === conceptId) continue;
            
            const cosine = this.cosineSimilarity(targetVector.dimensions, vector.dimensions);
            const euclidean = this.euclideanDistance(targetVector.dimensions, vector.dimensions);
            
            similarities.push({
                conceptId: id,
                name: vector.name,
                cosine,
                euclidean
            });
        }
        
        // Sort by cosine similarity (descending)
        similarities.sort((a, b) => b.cosine - a.cosine);
        
        return similarities.slice(0, limit);
    }

    // K-means clustering
    public kMeansClustering(spaceName: string, k: number, maxIterations: number = 100): ClusterAssignment[] {
        const space = this.semanticSpaces.get(spaceName);
        if (!space || space.concepts.size < k) return [];
        
        const concepts = Array.from(space.concepts.entries());
        
        // Initialize centroids randomly
        let centroids: number[][] = [];
        const indices = new Set<number>();
        while (centroids.length < k) {
            const idx = Math.floor(Math.random() * concepts.length);
            if (!indices.has(idx)) {
                indices.add(idx);
                centroids.push([...concepts[idx][1].dimensions]);
            }
        }
        
        let assignments: Map<string, number> = new Map();
        
        for (let iter = 0; iter < maxIterations; iter++) {
            const newAssignments: Map<string, number> = new Map();
            
            // Assign each concept to nearest centroid
            for (const [conceptId, vector] of space.concepts) {
                let minDistance = Infinity;
                let nearestCluster = 0;
                
                for (let i = 0; i < centroids.length; i++) {
                    const distance = this.euclideanDistance(vector.dimensions, centroids[i]);
                    if (distance < minDistance) {
                        minDistance = distance;
                        nearestCluster = i;
                    }
                }
                
                newAssignments.set(conceptId, nearestCluster);
            }
            
            // Update centroids
            for (let i = 0; i < k; i++) {
                const clusterConcepts = concepts.filter(([id]) => newAssignments.get(id) === i);
                if (clusterConcepts.length === 0) continue;
                
                const newCentroid = new Array(space.dimensions).fill(0);
                for (const [, vector] of clusterConcepts) {
                    for (let j = 0; j < space.dimensions; j++) {
                        newCentroid[j] += vector.dimensions[j];
                    }
                }
                
                for (let j = 0; j < space.dimensions; j++) {
                    newCentroid[j] /= clusterConcepts.length;
                }
                
                centroids[i] = newCentroid;
            }
            
            // Check convergence
            let changed = false;
            for (const [id, cluster] of newAssignments) {
                if (assignments.get(id) !== cluster) {
                    changed = true;
                    break;
                }
            }
            
            assignments = newAssignments;
            if (!changed) break;
        }
        
        // Build results
        const results: ClusterAssignment[] = [];
        for (const [conceptId, vector] of space.concepts) {
            const clusterId = assignments.get(conceptId) || 0;
            const distance = this.euclideanDistance(vector.dimensions, centroids[clusterId]);
            
            results.push({
                conceptId,
                clusterId,
                distance,
                confidence: 1 - (distance / (1 + distance))  // Normalized confidence
            });
        }
        
        return results;
    }

    // Get semantic space
    public getSemanticSpace(spaceName: string): SemanticSpace | undefined {
        return this.semanticSpaces.get(spaceName);
    }

    // Create new semantic space
    public createSemanticSpace(name: string, dimensionNames: string[]): void {
        const space: SemanticSpace = {
            name,
            dimensionNames,
            concepts: new Map(),
            dimensions: dimensionNames.length
        };
        
        this.semanticSpaces.set(name, space);
    }

    // Get all concepts in a space
    public getAllConcepts(spaceName: string): ConceptVector[] {
        const space = this.semanticSpaces.get(spaceName);
        if (!space) return [];
        
        return Array.from(space.concepts.values());
    }
}

